import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, LogIn, UserPlus, User as UserIcon, Clock } from "lucide-react";
import { apiLogin, apiRegister, extractErrorMsg } from "../lib/auth";
import { loadProfile } from "../lib/storage";
import { useAuth, INACTIVITY_FLAG_KEY } from "../contexts/AuthContext";
import { ClayButton } from "../components/ClayButton";
import { PageShell } from "../components/PageShell";

export default function AuthScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { afterAuth, continueAsGuest } = useAuth();
  const initialTab = searchParams.get("signup") === "1" ? "register" : "login";
  const [tab, setTab] = useState(initialTab);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inactivityNotice, setInactivityNotice] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(INACTIVITY_FLAG_KEY)) {
        setInactivityNotice(true);
        sessionStorage.removeItem(INACTIVITY_FLAG_KEY);
      }
    } catch {}
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (tab === "login") {
      if (!username.trim() || !password) {
        setError("Por favor llena ambos campos");
        return;
      }
    } else {
      if (!nombre.trim() || !apellido.trim() || !email.trim() || !username.trim() || !password) {
        setError("Por favor llena todos los campos");
        return;
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
        setError("Correo electrónico inválido");
        return;
      }
    }
    setLoading(true);
    try {
      let data;
      if (tab === "login") {
        data = await apiLogin(username, password);
      } else {
        // On register, send existing guest profile if any (so progress migrates)
        const guestProfile = loadProfile();
        data = await apiRegister({
          username: username.trim(),
          password,
          nombre: nombre.trim(),
          apellido: apellido.trim(),
          email: email.trim(),
          profile: guestProfile,
        });
      }
      afterAuth(data);
      // Navigate by profile existence: if cloud has profile -> dashboard; else profile setup
      if (data.user.profile) navigate("/dashboard", { replace: true });
      else navigate("/perfil", { replace: true });
    } catch (err) {
      setError(extractErrorMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = () => {
    continueAsGuest();
    const profile = loadProfile();
    navigate(profile ? "/dashboard" : "/perfil", { replace: true });
  };

  return (
    <PageShell>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center mt-6 sm:mt-10"
      >
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border-2 border-[#FFD700] rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 text-[#FF9500]" strokeWidth={3} />
            <span className="text-sm font-bold uppercase tracking-wider text-[#7A5C00]">
              MateConMar
            </span>
          </div>
          <button
            onClick={() => navigate("/")}
            className="absolute top-4 left-4 sm:top-6 sm:left-6 text-sm font-bold text-[#5C5C60] hover:text-[#1C1C1E] transition-colors"
            data-testid="auth-back-to-landing"
          >
            ← Inicio
          </button>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1C1E] mb-3">
            ¡Bienvenido! 👋
          </h1>
          <p className="text-base sm:text-lg text-[#5C5C60] max-w-xl">
            Inicia sesión para guardar tu progreso o juega como invitado.
          </p>
        </motion.div>

        <div className="w-full max-w-md bg-white rounded-[2rem] border-4 border-white shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)] p-6 sm:p-8">
          {inactivityNotice && (
            <div
              className="flex items-start gap-3 bg-[#FFF6CC] border-2 border-[#FFD700] rounded-2xl px-4 py-3 mb-5"
              data-testid="auth-inactivity-notice"
            >
              <Clock className="w-5 h-5 text-[#7A5C00] shrink-0 mt-0.5" strokeWidth={3} />
              <p className="text-sm font-bold text-[#7A5C00] leading-snug">
                Tu sesión se cerró automáticamente por inactividad (10 minutos).
                Vuelve a iniciar sesión cuando quieras.
              </p>
            </div>
          )}
          <div
            className="flex gap-2 bg-[#FFF6CC] rounded-full p-1 mb-6"
            data-testid="auth-tabs"
          >
            <button
              type="button"
              onClick={() => { setTab("login"); setError(""); }}
              className={`flex-1 py-2.5 rounded-full font-bold text-sm transition-colors ${
                tab === "login" ? "bg-white shadow text-[#1C1C1E]" : "text-[#7A5C00]"
              }`}
              data-testid="tab-login"
            >
              Iniciar sesión
            </button>
            <button
              type="button"
              onClick={() => { setTab("register"); setError(""); }}
              className={`flex-1 py-2.5 rounded-full font-bold text-sm transition-colors ${
                tab === "register" ? "bg-white shadow text-[#1C1C1E]" : "text-[#7A5C00]"
              }`}
              data-testid="tab-register"
            >
              Registrarme
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {tab === "register" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-xs font-bold uppercase tracking-wider text-[#5C5C60] mb-1.5">
                      Nombre
                    </span>
                    <input
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      placeholder="Lucía"
                      maxLength={40}
                      autoComplete="given-name"
                      className="w-full bg-[#FFF9E6] border-4 border-[#F2ECE4] rounded-2xl px-4 py-3 text-lg font-bold focus:border-[#FF9500] focus:outline-none transition-colors placeholder:text-gray-300 placeholder:font-normal"
                      data-testid="auth-nombre-input"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-bold uppercase tracking-wider text-[#5C5C60] mb-1.5">
                      Apellido
                    </span>
                    <input
                      type="text"
                      value={apellido}
                      onChange={(e) => setApellido(e.target.value)}
                      placeholder="García"
                      maxLength={40}
                      autoComplete="family-name"
                      className="w-full bg-[#FFF9E6] border-4 border-[#F2ECE4] rounded-2xl px-4 py-3 text-lg font-bold focus:border-[#FF9500] focus:outline-none transition-colors placeholder:text-gray-300 placeholder:font-normal"
                      data-testid="auth-apellido-input"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="block text-xs font-bold uppercase tracking-wider text-[#5C5C60] mb-1.5">
                    Correo
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ej. lucia@correo.com"
                    autoComplete="email"
                    className="w-full bg-[#FFF9E6] border-4 border-[#F2ECE4] rounded-2xl px-4 py-3 text-lg font-bold focus:border-[#FF9500] focus:outline-none transition-colors placeholder:text-gray-300 placeholder:font-normal"
                    data-testid="auth-email-input"
                  />
                </label>
              </>
            )}
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-wider text-[#5C5C60] mb-1.5">
                Usuario
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ej. lucia07"
                maxLength={20}
                autoComplete="username"
                className="w-full bg-[#FFF9E6] border-4 border-[#F2ECE4] rounded-2xl px-4 py-3 text-lg font-bold focus:border-[#FF9500] focus:outline-none transition-colors placeholder:text-gray-300 placeholder:font-normal"
                data-testid="auth-username-input"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-wider text-[#5C5C60] mb-1.5">
                Contraseña
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="mínimo 6 caracteres"
                autoComplete={tab === "login" ? "current-password" : "new-password"}
                className="w-full bg-[#FFF9E6] border-4 border-[#F2ECE4] rounded-2xl px-4 py-3 text-lg font-bold focus:border-[#FF9500] focus:outline-none transition-colors placeholder:text-gray-300 placeholder:font-normal"
                data-testid="auth-password-input"
              />
            </label>

            {error && (
              <div
                className="bg-[#FFEAEA] border-2 border-[#FF6B6B] rounded-2xl px-4 py-2.5 text-sm font-bold text-[#803535]"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <ClayButton
              type="submit"
              size="md"
              disabled={loading}
              className="w-full"
              data-testid="auth-submit-btn"
            >
              {tab === "login" ? <LogIn className="w-5 h-5" strokeWidth={3} /> : <UserPlus className="w-5 h-5" strokeWidth={3} />}
              {loading ? "Cargando..." : tab === "login" ? "Entrar" : "Crear cuenta"}
            </ClayButton>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-0.5 bg-gray-100" />
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">o</span>
            <div className="flex-1 h-0.5 bg-gray-100" />
          </div>

          <button
            type="button"
            onClick={handleGuest}
            className="w-full flex items-center justify-center gap-2 bg-white border-2 border-gray-200 hover:bg-gray-50 rounded-full px-5 py-3 text-base font-bold text-[#1C1C1E] transition-colors"
            data-testid="auth-guest-btn"
          >
            <UserIcon className="w-5 h-5" strokeWidth={3} />
            Continuar como invitado
          </button>
          <p className="text-xs text-center text-[#5C5C60] mt-3">
            En modo invitado tu progreso solo se guarda en este dispositivo.
          </p>
          <p className="text-xs text-center text-[#5C5C60] mt-2">
            Al continuar aceptas los{" "}
            <button
              type="button"
              onClick={() => navigate("/terminos")}
              className="underline font-bold hover:text-[#FF9500]"
              data-testid="auth-terms-link"
            >
              Términos y Política
            </button>
            .
          </p>
        </div>
      </motion.div>
    </PageShell>
  );
}
